import { useEffect, useState } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Checkbox,
  FormControlLabel,
  ListItemIcon,
  Stack,
  Tooltip,
} from '@mui/material'
import {
  Shuffle,
  CheckCircle,
  Upload,
  Delete,
  Download,
  AssignmentInd,
  Warning,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { hasPermission } from '../utils/permissions'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetRoundsQuery } from '../store/api/roundsApi'
import {
  useDrawBracketMutation,
  useGetLatestBracketQuery,
  useConfirmBracketMutation,
  useDeleteBracketMutation,
  useConfigureMatchMutation,
  useListRoleSheetsQuery,
  useUploadRoleSheetMutation,
  useDeleteRoleSheetMutation,
  useLazyDownloadRoleSheetQuery,
} from '../store/api/bracketsApi'
import { useGetUsersQuery } from '../store/api/usersApi'
import { formatCaseDisplay } from '../utils/caseDisplay'
import { getBracketDrawBlockedReason } from '../utils/r2'
import { PageHeader } from '../components/PageHeader'
import { getContentPaperSx, getFilterPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'
import StructuredRoleSheetDialog, {
  ROLE_LABELS,
} from '../components/StructuredRoleSheetDialog'

export default function BracketsPage() {
  const { user } = useAppSelector((s) => s.auth)
  const canManage = user && hasPermission(user.role, 'ASSIGN_CASES')
  // 2026-05-25: polling 15s — sau khi admin publish R1 ở ReportsPage,
  // BracketsPage cần thấy team.competition_status mới để cho phép bốc thăm
  // Bán kết. Trước đây fetch 1 lần on mount → user phải refresh tay.
  const { data: cases = [] } = useGetCasesQuery(undefined, { pollingInterval: 15000 })
  const { data: teams = [] } = useGetTeamsQuery(undefined, { pollingInterval: 15000 })
  const { data: rounds = [] } = useGetRoundsQuery(undefined, { pollingInterval: 15000 })
  const [caseId, setCaseId] = useState('')
  // UAT-Bán kết #1: chọn round + đội đủ điều kiện
  const [roundId, setRoundId] = useState<string>('')
  const [eligibleTeams, setEligibleTeams] = useState<string[]>([])

  // 2026-05-25: read URL params ?case=X&round=Y → set initial state.
  // CourtroomPage có nút "Bốc thăm cặp đấu" pass qua URL → trước đây user
  // landing page nhưng dropdown vẫn default case[0] + round trống → confusing.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const urlCase = searchParams.get('case')
    const urlRound = searchParams.get('round')
    if (urlCase && cases.length > 0 && cases.some((c) => c.id === urlCase) && caseId !== urlCase) {
      setCaseId(urlCase)
    } else if (cases.length && !caseId) {
      setCaseId(cases[0].id)
    }
    if (urlRound && rounds.length > 0 && rounds.some((r) => r.id === urlRound) && roundId !== urlRound) {
      setRoundId(urlRound)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cases, rounds, searchParams])

  // Sync state → URL khi user đổi dropdown — giúp deep-link copy/paste hoạt động.
  useEffect(() => {
    if (!caseId) return
    const next = new URLSearchParams(searchParams)
    next.set('case', caseId)
    if (roundId) next.set('round', roundId)
    else next.delete('round')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, roundId])

  // Bug fix 2026-05-25: scope theo round_id để mỗi vòng (memorial/semifinal/
  // final) có bracket riêng — không bị nhầm sang draft memorial cũ khi
  // user pick semifinal.
  const { data: bracket, refetch: refetchBracket } = useGetLatestBracketQuery(
    { case_id: caseId, round_id: roundId || undefined },
    { skip: !caseId },
  )
  const { data: sheets = [], refetch: refetchSheets } = useListRoleSheetsQuery(caseId, {
    skip: !caseId,
  })

  const [drawBracket, { isLoading: drawing }] = useDrawBracketMutation()
  const [confirmBracket, { isLoading: confirming }] = useConfirmBracketMutation()
  const [deleteBracket, { isLoading: deletingBracket }] = useDeleteBracketMutation()
  const [uploadSheet, { isLoading: uploading }] = useUploadRoleSheetMutation()
  const [deleteSheet] = useDeleteRoleSheetMutation()
  const [downloadSheet] = useLazyDownloadRoleSheetQuery()
  const [sheetTitle, setSheetTitle] = useState('')

  const handleDraw = async () => {
    if (!caseId) return
    try {
      await drawBracket({
        case_id: caseId,
        round_id: roundId || undefined,
        eligible_team_ids: eligibleTeams.length > 0 ? eligibleTeams : undefined,
      }).unwrap()
      refetchBracket()
    } catch (e: any) {
      alert(e?.data?.detail || e?.message || 'Bốc thăm thất bại')
    }
  }

  // Issue 2 (2026-05-25): configure-match dùng để set time / judges / link
  // phòng + gửi notify. List judges (admin user role=judge).
  const [configureMatch, { isLoading: configuring }] = useConfigureMatchMutation()
  const { data: judgeUsers = [] } = useGetUsersQuery({ role: 'judge' as any })

  // Lọc rounds + teams thuộc case đang chọn
  const caseRounds = rounds.filter((r: any) => r.case_id === caseId)
  const caseObj = cases.find((c) => c.id === caseId)
  const allCaseTeams = teams.filter((t) => caseObj?.assigned_team_ids?.includes(t.id))

  // R1→R2 doc Bước 5: M05 chỉ áp dụng cho đội đã advance (Top 4 từ Sơ loại)
  const selectedRoundType = (caseRounds.find((r) => r.id === roundId) as any)?.round_type
  const isHearingRound = selectedRoundType === 'semifinal' || selectedRoundType === 'final'
  const advancedTeams = allCaseTeams.filter((t: any) =>
    isHearingRound
      ? t.competition_status === 'advanced_to_semifinal' ||
        t.competition_status === 'advanced_to_final'
      : true,
  )
  const eliminatedTeams = allCaseTeams.filter((t: any) => t.competition_status === 'eliminated')
  // Khi đang chọn vòng Bán kết/Chung kết → restrict eligibleTeams chỉ Top 4
  const assignedTeams = isHearingRound ? advancedTeams : allCaseTeams
  const currentEligibleCount = eligibleTeams.length || assignedTeams.length
  // BE enforce: semifinal=4, final=2 — chặn nút ngay từ FE để UX tốt hơn.
  // Logic ở utils/r2.ts để Vitest test được mà không cần render component.
  const drawBlockedReason = getBracketDrawBlockedReason({
    caseId,
    roundType: selectedRoundType,
    eligibleCount: currentEligibleCount,
  })
  const bracketStats = [
    { label: 'Đội trong cuộc thi', value: allCaseTeams.length, color: 'primary' as const },
    { label: 'Đội đủ điều kiện', value: currentEligibleCount, color: 'success' as const },
    { label: 'Cặp đấu', value: bracket?.matches?.length || 0, color: 'secondary' as const },
    { label: 'Bảng phân vai', value: sheets.length, color: 'warning' as const },
  ]

  const handleConfirm = async () => {
    if (!bracket?.id) return
    if (!window.confirm('Xác nhận bracket? Sau khi xác nhận không thể chỉnh sửa.')) return
    try {
      await confirmBracket(bracket.id as string).unwrap()
      refetchBracket()
    } catch (e: any) {
      alert(e?.data?.detail || e?.message || 'Xác nhận thất bại')
    }
  }

  const handleUpload = async (file: File | null) => {
    if (!file || !caseId) return
    try {
      await uploadSheet({ caseId, title: sheetTitle || undefined, file }).unwrap()
      setSheetTitle('')
      refetchSheets()
    } catch (e: any) {
      alert(e?.data?.detail || e?.message || 'Upload thất bại')
    }
  }

  // ── Structured submission (M05-2): mở dialog 4 vai ─────────────────────
  const [structOpen, setStructOpen] = useState(false)

  if (!canManage) {
    return (
      <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <PageHeader
          title="Bốc thăm & bảng phân vai"
          subtitle="M05 là màn điều phối dành cho Ban Thư ký/BTC."
          icon={<Shuffle />}
        />
        <Alert severity="info">
          Bạn không có quyền điều phối bốc thăm. Sinh viên nộp bảng phân vai trong module Đơn khởi kiện theo cuộc thi được phân.
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Bốc thăm & bảng phân vai"
        subtitle="M05: chọn cuộc thi, bốc thăm cặp Nguyên đơn / Bị đơn và theo dõi bảng phân vai."
        icon={<Shuffle />}
      />

      <Paper
        elevation={0}
        sx={(theme) => ({
          ...getFilterPaperSx(theme),
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(260px, 1.2fr) minmax(220px, 0.9fr)',
            lg: 'minmax(320px, 1.25fr) minmax(240px, 0.9fr) minmax(360px, 1.35fr)',
          },
          alignItems: 'start',
        })}
      >
        <FormControl fullWidth>
          <InputLabel>Cuộc thi</InputLabel>
          <Select
            label="Cuộc thi"
            value={caseId}
            onChange={(e) => {
              setCaseId(e.target.value as string)
              setRoundId('')
              setEligibleTeams([])
            }}
          >
            {cases.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {formatCaseDisplay(c)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* UAT-Bán kết #1: chọn vòng cụ thể */}
        <FormControl fullWidth disabled={!caseId}>
          <InputLabel>Vòng (tuỳ chọn)</InputLabel>
          <Select
            label="Vòng (tuỳ chọn)"
            value={roundId}
            onChange={(e) => setRoundId(e.target.value as string)}
          >
            <MenuItem value=""><em>— Không gắn vòng —</em></MenuItem>
            {caseRounds.map((r: any) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name} ({r.round_type})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* UAT-Bán kết #1: multi-select đội đủ điều kiện cho vòng (VD: 4 đội qua sơ loại) */}
        <FormControl fullWidth disabled={!caseId}>
          <InputLabel>Đội đủ điều kiện (để trống = toàn bộ đội của vụ)</InputLabel>
          <Select
            multiple
            label="Đội đủ điều kiện (để trống = toàn bộ đội của vụ)"
            value={eligibleTeams}
            onChange={(e) => setEligibleTeams(e.target.value as string[])}
            renderValue={(sel) =>
              (sel as string[]).map((id) => teams.find((t) => t.id === id)?.name || id).join(', ') ||
              '(toàn bộ đội)'
            }
          >
            {assignedTeams.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                <ListItemIcon>
                  <Checkbox checked={eligibleTeams.indexOf(t.id) > -1} edge="start" />
                </ListItemIcon>
                <ListItemText primary={t.name} secondary={t.university} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {eligibleTeams.length > 0 && eligibleTeams.length % 2 !== 0 && (
          <Alert severity="warning" sx={{ gridColumn: '1 / -1' }}>
            Số đội đủ điều kiện ({eligibleTeams.length}) không chẵn — hệ thống yêu cầu chẵn để ghép cặp.
          </Alert>
        )}
        {/* R1→R2 doc Bước 5: M05 chỉ cho Top 4 đội đã advance */}
        {isHearingRound && advancedTeams.length === 0 && (
          <Alert severity="warning" icon={<Warning />} sx={{ gridColumn: '1 / -1' }}>
            Vòng này chưa có đội nào được công bố vào Bán kết/Chung kết. Hãy hoàn tất công bố
            kết quả vòng trước (ReportsPage) để mở M05 cho Top 4.
          </Alert>
        )}
        {isHearingRound && advancedTeams.length > 0 && (
          <Alert severity="success" icon={<CheckCircle />} sx={{ gridColumn: '1 / -1' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <span>
                <b>{advancedTeams.length} đội</b> đã được công bố vào vòng này:
              </span>
              {advancedTeams.map((t) => (
                <Chip
                  key={t.id}
                  size="small"
                  label={t.name}
                  color="success"
                  variant="outlined"
                />
              ))}
              {eliminatedTeams.length > 0 && (
                <Tooltip title={eliminatedTeams.map((t) => t.name).join(', ')}>
                  <Chip
                    size="small"
                    label={`${eliminatedTeams.length} đội đã loại`}
                    color="default"
                  />
                </Tooltip>
              )}
            </Stack>
          </Alert>
        )}
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          mb: 3,
        }}
      >
        {bracketStats.map((item) => (
          <Paper
            key={item.label}
            elevation={0}
            sx={(theme) => ({
              p: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              minHeight: 86,
            })}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                  {item.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {item.label}
                </Typography>
              </Box>
              <Chip size="small" color={item.color} label="M05" variant="outlined" />
            </Stack>
          </Paper>
        ))}
      </Box>

      <Paper elevation={0} sx={(theme) => ({ ...getContentPaperSx(theme), p: 3, mb: 3 })}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Bracket
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ghép cặp tranh tụng và khóa kết quả bốc thăm cho vòng đã chọn.
            </Typography>
          </Box>
          {canManage && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
              <Tooltip title={drawBlockedReason || ''} disableHoverListener={!drawBlockedReason}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Shuffle />}
                    onClick={handleDraw}
                    disabled={drawing || !!drawBlockedReason}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Bốc thăm lại
                  </Button>
                </span>
              </Tooltip>
              {/* 2026-05-25: nút Xoá bản nháp — chỉ hiện khi bracket draft.
                  Cần thiết vì BE reject draw nếu đã có draft (anti
                  double-draw) → user kẹt không bốc lại được seed mới. */}
              {bracket?.id && bracket.status === 'draft' && (
                <Tooltip title="Xoá bracket draft hiện tại để có thể bốc thăm lại với seed mới hoặc thay đổi danh sách đội đủ điều kiện">
                  <span>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      disabled={deletingBracket}
                      onClick={async () => {
                        if (!bracket?.id) return
                        if (!window.confirm(
                          'Xoá bracket bản nháp hiện tại? Hành động này không thể hoàn tác. ' +
                          'Sau khi xoá bạn có thể bốc thăm lại với seed mới hoặc danh sách đội khác.',
                        )) return
                        try {
                          await deleteBracket(bracket.id).unwrap()
                          refetchBracket()
                        } catch (e: any) {
                          alert(e?.data?.detail || e?.message || 'Xoá bản nháp thất bại')
                        }
                      }}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      {deletingBracket ? 'Đang xoá...' : 'Xoá bản nháp'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button
                variant="outlined"
                color="success"
                startIcon={<CheckCircle />}
                onClick={handleConfirm}
                disabled={confirming || !bracket?.id || bracket.status === 'confirmed' || !bracket.id}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Xác nhận bracket
              </Button>
            </Stack>
          )}
        </Stack>
        {!canManage && (
          <Alert severity="info">Chỉ BTC (quyền phân công cuộc thi) mới bốc thăm / xác nhận.</Alert>
        )}
        {!bracket && <Alert severity="warning">Chưa có bracket cho cuộc thi này.</Alert>}
        {bracket && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                label={bracket.status === 'confirmed' ? 'Đã khóa (confirmed)' : 'Bản nháp'}
                color={bracket.status === 'confirmed' ? 'success' : 'default'}
              />
              {bracket.round_type && (
                <Chip
                  label={`Vòng: ${bracket.round_type}`}
                  color="primary"
                  variant="outlined"
                />
              )}
              {bracket.eligible_team_ids && bracket.eligible_team_ids.length > 0 && (
                <Chip
                  label={`${bracket.eligible_team_ids.length} đội đủ điều kiện`}
                  variant="outlined"
                />
              )}
            </Box>
            <TableContainer component={Paper} elevation={0} sx={(theme) => getTablePaperSx(theme)}>
              <Table size="small" sx={{ minWidth: 620 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: 'background.default', whiteSpace: 'nowrap' } }}>
                    <TableCell sx={{ width: 72 }}>#</TableCell>
                    <TableCell>Nguyên đơn</TableCell>
                    <TableCell>Bị đơn</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bracket.matches.map((m, idx) => {
                    const tp = teams.find((t) => t.id === m.team_plaintiff_id)
                    const td = teams.find((t) => t.id === m.team_defendant_id)
                    return (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Chip size="small" label={`Cặp ${idx + 1}`} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {tp?.name || '— Đội chưa rõ —'}
                          </Typography>
                          {tp?.university && (
                            <Typography variant="caption" color="text.secondary">
                              {tp.university}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {td?.name || '— Đội chưa rõ —'}
                          </Typography>
                          {td?.university && (
                            <Typography variant="caption" color="text.secondary">
                              {td.university}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* 2026-05-25 (Issue 2): Cấu hình từng cặp sau khi confirm.
                Chỉ hiển thị khi bracket đã confirm và là vòng Bán kết/Chung kết —
                R1 không có cặp đấu courtroom. */}
            {bracket.status === 'confirmed' &&
              (bracket.round_type === 'semifinal' || bracket.round_type === 'final') &&
              canManage && (
                <Box sx={{ mt: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6" fontWeight={700}>
                      Cấu hình phòng xử cho từng cặp
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      color="info"
                      label="Thời gian · Giám khảo · Link phòng · Notify"
                    />
                  </Stack>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Mỗi cặp đã được tự sinh 1 phòng courtroom riêng. BTC nhập
                    lịch + chọn giám khảo + link phòng (Daily / Zoom / Meet),
                    bấm <b>Lưu & Gửi thông báo</b> để gửi email + in-app cho
                    đội + GK.
                  </Alert>
                  <Stack spacing={2}>
                    {bracket.matches.map((m: any, idx: number) => (
                      <MatchConfigCard
                        key={(m.match_id as string) || `${bracket.id}::m${idx}`}
                        bracket={bracket}
                        matchIndex={idx}
                        match={m}
                        teams={teams as any}
                        judgeUsers={judgeUsers as any}
                        configuring={configuring}
                        onConfigure={async (payload) => {
                          try {
                            const res = await configureMatch(payload).unwrap()
                            refetchBracket()
                            alert(
                              `Đã lưu cấu hình cặp #${idx + 1}.` +
                                (payload.notify
                                  ? ` Đã gửi thông báo cho ${res.notified_count} người.`
                                  : ''),
                            )
                            return res
                          } catch (e: any) {
                            alert(e?.data?.detail || e?.message || 'Lưu thất bại')
                          }
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
          </>
        )}
      </Paper>

      <Paper elevation={0} sx={(theme) => ({ ...getContentPaperSx(theme), p: 3 })}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Bảng phân vai
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Đội nộp 4 vai cho vòng đã chọn; BTC có thể upload file PDF bổ sung.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          {/* Structured form — đội nộp 4 vai online */}
          <Button
            variant="contained"
            startIcon={<AssignmentInd />}
            disabled={!caseId || !roundId}
            onClick={() => setStructOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Nộp Bảng phân vai (4 vai)
          </Button>
          {canManage && (
            <>
              <TextField
                label="Tiêu đề (tuỳ chọn)"
                value={sheetTitle}
                onChange={(e) => setSheetTitle(e.target.value)}
                size="small"
                sx={{ minWidth: { sm: 220 } }}
              />
              <Button
                variant="outlined"
                component="label"
                startIcon={<Upload />}
                disabled={uploading || !caseId}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Upload PDF (BTC)
                <input
                  type="file"
                  hidden
                  accept=".pdf,application/pdf"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    e.target.value = ''
                    void handleUpload(f || null)
                  }}
                />
              </Button>
            </>
          )}
          </Stack>
        </Stack>
        {!roundId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Chọn vòng ở khung lọc phía trên trước khi nộp bảng phân vai.
          </Alert>
        )}
        <List
          dense
          disablePadding
          sx={{
            display: 'grid',
            gap: 1.5,
          }}
        >
          {sheets.length === 0 && (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Chưa có bảng phân vai.
              </Typography>
            </Paper>
          )}
          {sheets.map((s) => (
            <ListItem
              key={s.id}
              sx={(theme) => ({
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                px: 2,
                py: 1.5,
                bgcolor: 'background.paper',
                alignItems: 'flex-start',
                '& .MuiListItemSecondaryAction-root': {
                  right: 12,
                  top: 12,
                  transform: 'none',
                },
              })}
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  {/* Structured sheets không có file đính kèm → ẩn nút download. */}
                  {s.submission_kind !== 'structured' && s.storage_path && (
                    <IconButton
                      edge="end"
                      aria-label="download"
                      onClick={async () => {
                        try {
                          const blob = await downloadSheet(s.id!).unwrap()
                          const url = window.URL.createObjectURL(blob)
                          const a = document.createElement('a')
                          a.href = url
                          a.download = s.file_name || 'role-sheet.pdf'
                          document.body.appendChild(a)
                          a.click()
                          window.URL.revokeObjectURL(url)
                          document.body.removeChild(a)
                        } catch (e: any) {
                          alert(e?.data?.detail || e?.message || 'Tải thất bại')
                        }
                      }}
                    >
                      <Download />
                    </IconButton>
                  )}
                  {canManage && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={async () => {
                        if (!window.confirm('Xóa bảng phân vai?')) return
                        try {
                          await deleteSheet(s.id!).unwrap()
                          refetchSheets()
                        } catch (e: any) {
                          alert(e?.data?.detail || e?.message)
                        }
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Stack>
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <span>{s.title}</span>
                    {s.representing_role && (
                      <Chip
                        size="small"
                        label={s.representing_role === 'plaintiff' ? 'Nguyên đơn' : 'Bị đơn'}
                        color={s.representing_role === 'plaintiff' ? 'primary' : 'secondary'}
                      />
                    )}
                    {s.is_late === true && (
                      <Tooltip title={`Nộp muộn ${s.minutes_late || 0} phút — bị trừ 20đ`}>
                        <Chip size="small" color="warning" icon={<Warning />} label="Muộn −20đ" />
                      </Tooltip>
                    )}
                    {s.is_late === false && (
                      <Chip size="small" color="success" icon={<CheckCircle />} label="Đúng hạn" />
                    )}
                  </Stack>
                }
                secondary={
                  s.roles && s.roles.length > 0 ? (
                    <Box sx={{ mt: 0.5 }}>
                      {s.roles.map((r) => (
                        <Typography key={r.position} variant="caption" display="block">
                          • {ROLE_LABELS[r.position]}: <b>{r.full_name}</b>
                          {r.student_id ? ` (MSSV ${r.student_id})` : ''}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    s.file_name
                  )
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Structured submission dialog */}
      <StructuredRoleSheetDialog
        open={structOpen}
        onClose={() => setStructOpen(false)}
        caseId={caseId}
        roundId={roundId}
        teams={assignedTeams}
        roundLabel={selectedRoundType === 'final' ? 'Chung kết' : 'Bán kết'}
        onSubmitted={() => {
          setStructOpen(false)
          refetchSheets()
        }}
      />
    </Container>
  )
}

// ════════════════════════════════════════════════════════════════════
// MatchConfigCard — cấu hình 1 cặp đấu (time + judges + link phòng)
// ════════════════════════════════════════════════════════════════════
import { useState as _useState, useEffect as _useEffect } from 'react'
import { OpenInNew, Save, Schedule as _Schedule } from '@mui/icons-material'

function MatchConfigCard({
  bracket,
  matchIndex,
  match,
  teams,
  judgeUsers,
  configuring,
  onConfigure,
}: {
  bracket: any
  matchIndex: number
  match: any
  teams: any[]
  judgeUsers: any[]
  configuring: boolean
  onConfigure: (payload: {
    bracket_id: string
    match_id: string
    scheduled_at?: string | null
    judge_ids?: string[] | null
    meeting_link?: string | null
    auto_create_daily?: boolean
    notify?: boolean
  }) => Promise<{ meeting_link?: string | null } | void>
}) {
  const matchId = (match.match_id as string) || `${bracket.id}::m${matchIndex}`
  const tp = teams.find((t) => t.id === match.team_plaintiff_id)
  const td = teams.find((t) => t.id === match.team_defendant_id)

  // Local state — init từ existing match data
  const [scheduledAt, setScheduledAt] = _useState<string>(
    match.scheduled_at ? toLocalInput(match.scheduled_at) : '',
  )
  const [selectedJudges, setSelectedJudges] = _useState<string[]>(match.judge_ids || [])
  const [meetingLink, setMeetingLink] = _useState<string>('')
  // 2026-05-25: BE LUÔN auto-create Daily room + LUÔN gửi notify khi bấm
  // "Lưu & Gửi thông báo" — theo yêu cầu user simplify UX (bỏ 2 checkbox).
  // Nếu user nhập meetingLink thủ công → BE dùng link đó (không call Daily).
  // Nếu để trống → BE tự gọi DailyService.create_room cho match này.

  // Sync khi match data refetch
  _useEffect(() => {
    setScheduledAt(match.scheduled_at ? toLocalInput(match.scheduled_at) : '')
    setSelectedJudges(match.judge_ids || [])
  }, [match.scheduled_at, JSON.stringify(match.judge_ids || [])])

  const published = match.status === 'published' || !!match.winner_team_id
  const courtroomLink = match.courtroom_id ? `/courtroom?courtroom_id=${match.courtroom_id}` : '/courtroom'

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        sx={{ mb: 1.5, flexWrap: 'wrap' }}
        alignItems={{ md: 'center' }}
      >
        <Chip
          size="small"
          label={`Cặp #${matchIndex + 1}`}
          color="primary"
          sx={{ fontWeight: 700 }}
        />
        <Typography variant="body2" fontWeight={700}>
          {tp?.name || '—'} <span style={{ opacity: 0.5 }}>(Nguyên đơn)</span>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          vs
        </Typography>
        <Typography variant="body2" fontWeight={700}>
          {td?.name || '—'} <span style={{ opacity: 0.5 }}>(Bị đơn)</span>
        </Typography>
        {published && (
          <Chip
            size="small"
            color="success"
            label="Đã có winner — không thể sửa"
          />
        )}
      </Stack>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mt: 1 }}
        alignItems="flex-start"
      >
        <TextField
          label="Thời gian phiên xử"
          type="datetime-local"
          size="small"
          value={scheduledAt}
          onChange={(e) => setScheduledAt(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 220 }}
          disabled={published}
        />
        <FormControl size="small" sx={{ minWidth: 240, flex: 1 }} disabled={published}>
          <InputLabel>Giám khảo (chọn thủ công)</InputLabel>
          <Select
            multiple
            label="Giám khảo (chọn thủ công)"
            value={selectedJudges}
            onChange={(e) =>
              setSelectedJudges(
                typeof e.target.value === 'string'
                  ? e.target.value.split(',')
                  : (e.target.value as string[]),
              )
            }
            renderValue={(sel) =>
              (sel as string[])
                .map((id) => {
                  const u = judgeUsers.find((j: any) => j.id === id)
                  return u?.full_name || u?.username || id.slice(0, 6)
                })
                .join(', ')
            }
          >
            {judgeUsers.length === 0 ? (
              <MenuItem disabled value="">
                — Chưa có giám khảo —
              </MenuItem>
            ) : (
              judgeUsers.map((j: any) => (
                <MenuItem key={j.id} value={j.id}>
                  <Checkbox checked={selectedJudges.includes(j.id)} />
                  <ListItemIcon sx={{ minWidth: 0, mr: 1 }} />
                  {j.full_name || j.username || j.email}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Stack>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mt: 2 }}
        alignItems="flex-start"
      >
        <TextField
          label="Link phòng (để trống = hệ thống tự tạo Daily room)"
          size="small"
          fullWidth
          value={meetingLink}
          onChange={(e) => setMeetingLink(e.target.value)}
          placeholder="Để trống → bấm Lưu sẽ tự tạo phòng riêng cho cặp này"
          disabled={published}
          helperText="BTC có thể paste link Zoom/Meet riêng. Để trống → BE tự gọi Daily.co tạo phòng riêng cho cặp đấu (KHÔNG chia sẻ với cặp khác)."
        />
      </Stack>
      {match.courtroom_id && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Courtroom: <code>{match.courtroom_id}</code>
        </Typography>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
        <Button
          variant="contained"
          color="primary"
          startIcon={<Save />}
          disabled={configuring || published}
          onClick={async () => {
            const isoOrNull = scheduledAt
              ? new Date(scheduledAt).toISOString()
              : null
            const trimmedLink = meetingLink.trim()
            // 2026-05-25 simplify: bấm "Lưu & Gửi thông báo" → BE LUÔN tự
            // tạo Daily room (nếu user không nhập link manual) + LUÔN gửi
            // notify scoped theo match_id (chỉ 2 đội + judges của cặp đó).
            const res = await onConfigure({
              bracket_id: bracket.id,
              match_id: matchId,
              scheduled_at: isoOrNull,
              judge_ids: selectedJudges,
              // Nếu user paste link manual → dùng link đó, BE skip Daily auto.
              // Nếu để trống → BE tự gọi DailyService.create_room.
              meeting_link: trimmedLink || null,
              auto_create_daily: !trimmedLink,
              notify: true,
            })
            // 2026-05-25: hiện link mới tạo vào ô link phòng — UX feedback
            // user yêu cầu. BE response có `meeting_link` (cả manual paste
            // lẫn auto Daily). Set lại state để input field show link.
            const newLink = res && (res as any).meeting_link
            if (newLink && typeof newLink === 'string') {
              setMeetingLink(newLink)
            }
          }}
        >
          Lưu & Gửi thông báo
        </Button>
        <Button
          variant="outlined"
          startIcon={<OpenInNew />}
          component={RouterLink as any}
          to={courtroomLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          Mở phòng xử
        </Button>
      </Stack>
    </Paper>
  )
}

function toLocalInput(iso: string): string {
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    const tzOffset = d.getTimezoneOffset() * 60000
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16)
  } catch {
    return ''
  }
}
